import React, { useMemo } from "react";

/**
 * ✅ StatBox — Professional UI/UX Upgrade
 * - Same clean dashboard palette (white/gray + blue)
 * - Better spacing + subtle border + hover lift
 * - Optional: subtitle, trend, loading, compact mode
 * - Safe defaults (no breaking changes for your existing usage)
 *
 * Usage (existing):
 * <StatBox icon={FaUser} title="Users" value="120" iconColor="text-blue-600" />
 *
 * Optional:
 * <StatBox subtitle="Last 30 days" trend={{ value: 12.4, label: "vs last month" }} />
 */

function StatBox({
  icon: Icon,
  title,
  value,
  iconColor = "text-blue-600",
  subtitle,
  trend, // { value: number, label?: string }  value > 0 = up, < 0 = down
  loading = false,
  compact = false,
  onClick,
}) {
  const trendMeta = useMemo(() => {
    if (!trend || typeof trend.value !== "number") return null;
    const isUp = trend.value >= 0;
    return {
      isUp,
      text: `${isUp ? "+" : ""}${trend.value.toFixed(1)}%`,
      wrap: isUp
        ? "bg-green-50 text-green-700 border-green-200"
        : "bg-red-50 text-red-700 border-red-200",
      label: trend.label || (isUp ? "up" : "down"),
    };
  }, [trend]);

  return (
    <div
      role={onClick ? "button" : "group"}
      tabIndex={onClick ? 0 : -1}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      className={[
        "relative overflow-hidden rounded-xl bg-white",
        "border border-gray-200 shadow-sm",
        "transition-all duration-200",
        onClick ? "cursor-pointer hover:shadow-md hover:-translate-y-[1px]" : "hover:shadow-md",
        compact ? "p-4" : "p-6",
        "focus:outline-none focus:ring-2 focus:ring-blue-200",
      ].join(" ")}
    >
      {/* subtle top accent */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-80" />

      <div className="flex items-start justify-between gap-4">
        {/* Left */}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
              {title}
            </h3>

            {trendMeta && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${trendMeta.wrap}`}
                title={trendMeta.label}
              >
                <span className="leading-none">{trendMeta.isUp ? "▲" : "▼"}</span>
                <span className="leading-none">{trendMeta.text}</span>
              </span>
            )}
          </div>

          {subtitle ? (
            <p className="mt-1 text-xs text-gray-500 truncate">{subtitle}</p>
          ) : null}

          {/* Value */}
          {loading ? (
            <div className="mt-3 h-8 w-40 rounded bg-gray-100 animate-pulse" />
          ) : (
            <p
              className={[
                "mt-3 font-bold text-gray-900",
                compact ? "text-2xl" : "text-3xl",
              ].join(" ")}
            >
              {value}
            </p>
          )}
        </div>

        {/* Right icon */}
        <div className="flex items-center">
          <div
            className={[
              "rounded-xl border border-gray-200 bg-gray-50",
              "flex items-center justify-center",
              compact ? "w-10 h-10" : "w-12 h-12",
            ].join(" ")}
          >
            {Icon ? (
              <span className={[iconColor, compact ? "text-xl" : "text-2xl"].join(" ")}>
                <Icon />
              </span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatBox;